import { View } from 'react-big-calendar';
import { components } from 'react-select';

import { Arrow } from 'components/collapse/style';
import CommonSelect from 'components/select/CustomSelect';
import {
  CalendarInput,
  FileUpload,
  RadiusConfig,
  Row,
  Select,
  TextInput,
  Textarea,
  Typography,
  formatDate,
  openModal,
} from 'enable-ui';

import { MODAL_KEY } from 'modules/auth/core/modal-key';
import { DeleteRecurringEventModal } from 'modules/calendar/components/DeleteRecurringEventModal';
import { DeleteRecurringNotUpdatedEventModal } from 'modules/calendar/components/DeleteRecurringNotUpdatedEventModal';
import { EditRecurringEventModal } from 'modules/calendar/components/EditRecurringEventModal';
import { EditRecurringNotUpdatedEventModal } from 'modules/calendar/components/EditRecurringNotUpdatedEventModal';
import { EndRepeatModal } from 'modules/calendar/components/EndRepeatModal';
import InviteeSelector from 'modules/calendar/components/InviteeSelector';
import QRCodeModal from 'modules/calendar/components/QRCodeModal';
import { ExerciseContentType } from 'modules/classes/core/enum';
import { Classes } from 'modules/classes/core/type';

import {
  ButtonCreate,
  CustomCalendarInput,
  CustomFileUpload,
  GenerateQRButton,
  NameInput,
  RecurringSelect,
  SelectEventType,
  ViewQRButton,
} from '../styles/event';

import { ReactComponent as DescriptionIcon } from 'assets/icons/align-justified.svg';
import { ReactComponent as LessonIcon } from 'assets/icons/book.svg';
import { ReactComponent as CalendarIcon } from 'assets/icons/calendar.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { ReactComponent as CollapseIcon } from 'assets/icons/collapse-down.svg';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg';
import { ReactComponent as UserIcon } from 'assets/icons/learners.svg';
import { ReactComponent as AddressIcon } from 'assets/icons/map-pin.svg';
import { ReactComponent as NextIcon } from 'assets/icons/next.svg';
import { ReactComponent as NotiIcon } from 'assets/icons/notification.svg';
import { ReactComponent as PhotoIcon } from 'assets/icons/photo.svg';
import { ReactComponent as PlusIcon } from 'assets/icons/plus_blue.svg';
import { ReactComponent as QRCodeIcon } from 'assets/icons/qr.svg';
import { ReactComponent as ClassIcon } from 'assets/icons/slide.svg';
import { ReactComponent as LevelIcon } from 'assets/icons/stairs.svg';
import { ReactComponent as CategoryIcon } from 'assets/icons/tag.svg';
import { ReactComponent as UploadIcon } from 'assets/icons/upload.svg';

import { isEmpty, isNil, isUndefined } from 'lodash';

import { COLOR, COLOR_CONFIG } from 'constants/color';

import { useEventFormController } from '../core/controller/event-form';
import { EventType } from '../core/enum';

type RowItemProps = {
  icon: React.ElementType;
  children?: React.ReactNode;
  isDisabled?: boolean;
  iconContainerStyle?: React.CSSProperties;
};

const CustomDropdownIndicator = (props: any) => {
  return (
    <components.DropdownIndicator {...props}>
      <Arrow isOpen={props.isOpen} />
    </components.DropdownIndicator>
  );
};

const RowItem: React.FC<RowItemProps> = ({
  icon: Icon,
  children,
  isDisabled,
  iconContainerStyle,
}) => (
  <Row style={{ alignItems: 'center', cursor: isDisabled ? 'not-allowed' : 'pointer' }}>
    <div style={{ marginRight: 16, ...iconContainerStyle }}>
      <Icon />
    </div>
    {children}
  </Row>
);

export const EventForm = ({
  classes,
  view,
  drawer,
  selectedDate,
  eventSession,
  setCurrentDate,
}: {
  classes: Classes[];
  view: View;
  drawer: {
    value: boolean;
    setValue: React.Dispatch<React.SetStateAction<boolean>>;
  };
  selectedDate: Date;
  eventSession: {
    value: string;
    setValue: React.Dispatch<React.SetStateAction<string>>;
  };
  setCurrentDate: React.Dispatch<React.SetStateAction<Date>>;
}) => {
  const {
    onChangeEventType,
    eventOptions,
    recurringRuleOptions,
    onChangeRecurringRule,
    isReminder,
    onChangeClass,
    selectedClass,
    onChangeEventName,
    formState,
    handleChangeStartDate,
    handleChangeStartTime,
    handleChangeEndTime,
    contents,
    onChangeContent,
    handleChangeDescription,
    selectedContent,
    stations,
    onChangeStation,
    selectedStation,
    handleChangeEndDate,
    categoyOptions,
    onChangeCategory,
    levelOptions,
    onChangeLevel,
    onChangeThumbnail,
    thumbnailUrl,
    disabledSubmitBtn,
    selectedInvitees,
    setSelectedInvitees,
    handleRemoveThumbnail,
    inviteeState,
    getListClients,
    getListTrainers,
    handleTabChange,
    endRepeatValue,
    onChangeOccurrences,
    onChangeDate,
    onChangeEndRepeatValue,
    onChangeApplyOption,
    handleEndRepeatSubmit,
    handleCloseModal,
    handleClickSaveBtn,
    handleCreateEvent,
    handleSubmitEditRecurringEvent,
    handleGetWidthAndHeightThumbnail,
    handleDropdownOpen,
    handleInviteeScroll,
    handleClickDelete,
    handleDeleteEvent,
    errorMessage,
    handleGenerateQRCode,
    calendarKey,
  } = useEventFormController(classes, view, eventSession, selectedDate, drawer, setCurrentDate);

  const eventSessionId = eventSession.value;

  const eventTypeStyle = () => {
    switch (Number(formState.eventType)) {
      case EventType.Class:
        return {
          borderColor: COLOR.purpel['purpel-3'],
          color: COLOR.purpel['purpel-6'],
          backgroundColor: COLOR.purpel['purpel-1'],
        };

      case EventType.Gamification:
        return {
          borderColor: COLOR_CONFIG['cyan-suble'],
          color: COLOR_CONFIG.cyan,
          backgroundColor: COLOR_CONFIG['cyan-subtle'],
        };

      case EventType.Other:
        return {
          borderColor: COLOR_CONFIG['magenta-suble'],
          color: COLOR_CONFIG.magenta,
          backgroundColor: COLOR_CONFIG['magenta-subtle'],
        };

      default:
        return {};
    }
  };

  const getLabel = (title: string, subtitle: string) => (
    <div>
      <Typography variant="body-2" color={COLOR.surfaceNeutralBold['neutral-11']}>
        {title}
      </Typography>
      <Typography variant="paragraph" color={COLOR_CONFIG['neutral-text']}>
        {subtitle}
      </Typography>
    </div>
  );

  return (
    <div style={{ padding: '32px 24px 0 24px' }}>
      <Row>
        <SelectEventType
          disabled={!isEmpty(eventSessionId)}
          borderColor={eventTypeStyle().borderColor}
          color={eventTypeStyle().color}
          backgroundColor={eventTypeStyle().backgroundColor}
        >
          <Select
            options={eventOptions}
            onChange={onChangeEventType}
            selectedKeys={String(formState.eventType)}
            disabled={!isEmpty(eventSessionId) ? true : false}
          />
        </SelectEventType>

        {!isEmpty(eventSessionId) &&
          (!isEmpty(formState.qrKey) ? (
            <ViewQRButton onClick={() => openModal(MODAL_KEY.QR_CODE)}>
              <QRCodeIcon />
              View check-in QR
            </ViewQRButton>
          ) : (
            <GenerateQRButton onClick={handleGenerateQRCode}>
              <PlusIcon />
              Generate a check-in QR
            </GenerateQRButton>
          ))}
      </Row>

      <Row style={{ marginBottom: 56, marginTop: 8 }}>
        <NameInput>
          <TextInput
            placeholder="Enter event name"
            value={formState.eventName}
            onChange={onChangeEventName}
          />
        </NameInput>

        {!isEmpty(eventSessionId) ? (
          <div style={{ display: 'flex', flexDirection: 'row', gap: 8, alignItems: 'center' }}>
            <ButtonCreate onClick={handleClickSaveBtn} disabled={disabledSubmitBtn}>
              Save
            </ButtonCreate>
            <DeleteIcon className="cursor-pointer" onClick={handleClickDelete} />
          </div>
        ) : (
          <ButtonCreate onClick={handleCreateEvent} disabled={disabledSubmitBtn}>
            Create
          </ButtonCreate>
        )}
      </Row>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 24,
          height: 'calc(100vh - 171px)',
          overflow: 'hidden auto',
          scrollbarGutter: 'stable',
        }}
      >
        <div>
          <RowItem
            icon={CalendarIcon}
            iconContainerStyle={{
              transform:
                formState.eventType === EventType.Gamification
                  ? 'translateY(-10px)'
                  : 'translateY(0px)',
            }}
          >
            {Number(formState.eventType) !== EventType.Gamification ? (
              <div style={{ display: 'flex', flexDirection: 'row', gap: 12, alignItems: 'center' }}>
                <CustomCalendarInput>
                  <CalendarInput
                    key={calendarKey.value}
                    placeholder="Select date"
                    date={formState.startDate || selectedDate}
                    onChange={(value) => handleChangeStartDate(value)}
                  />
                </CustomCalendarInput>

                <TextInput
                  type="time"
                  value={formState.startTime}
                  onChange={handleChangeStartTime}
                />
                <div>
                  <NextIcon color={COLOR_CONFIG['neutral-text']} />
                </div>
                <TextInput type="time" value={formState.endTime} onChange={handleChangeEndTime} />
              </div>
            ) : (
              <Row direction="column" className="gap-6">
                <div
                  style={{ display: 'flex', flexDirection: 'row', gap: 12, alignItems: 'center' }}
                >
                  <CustomCalendarInput>
                    <CalendarInput
                      key={calendarKey.value}
                      placeholder="Select date"
                      date={formState.startDate || selectedDate}
                      onChange={(value) => handleChangeStartDate(value)}
                    />
                  </CustomCalendarInput>
                  <div>
                    <NextIcon color={COLOR_CONFIG['neutral-text']} />
                  </div>
                  <CustomCalendarInput>
                    <CalendarInput
                      key={calendarKey.value}
                      placeholder="Select date"
                      date={formState.endDate}
                      onChange={(value) => handleChangeEndDate(value)}
                    />
                  </CustomCalendarInput>
                </div>

                <Row direction="column">
                  {Number(formState.eventType) === EventType.Gamification && (
                    <Typography variant="body-3" color={COLOR_CONFIG['neutral-text']}>
                      Limit: 14 days
                    </Typography>
                  )}

                  {!isEmpty(errorMessage.selectedDate) && (
                    <Typography variant="body-3" color={COLOR_CONFIG.red}>
                      {errorMessage.selectedDate}
                    </Typography>
                  )}
                </Row>
              </Row>
            )}
          </RowItem>
        </div>

        {Number(formState.eventType) === EventType.Other && (
          <div>
            <RecurringSelect>
              {formState.recurringRule === 'none' ? (
                <CommonSelect
                  components={{
                    DropdownIndicator: CustomDropdownIndicator,
                  }}
                  options={recurringRuleOptions}
                  onChange={onChangeRecurringRule}
                  value={
                    recurringRuleOptions
                      .map(({ key, label }) => ({ value: key, label }))
                      .find((option) => option.value === formState.recurringRule) || null
                  }
                  onInputChange={() => {}}
                  controlStyle={{
                    minWidth: 243,
                    borderRadius: RadiusConfig.sm,
                  }}
                  extraStyle={{
                    indicatorSeparator: (styles) => ({
                      ...styles,
                      display: 'none',
                    }),
                    container: (styles) => ({
                      ...styles,
                      minWidth: 243,
                    }),
                  }}
                  isSearchable={false}
                />
              ) : (
                <div className="flex" style={{ gap: 12 }}>
                  <CommonSelect
                    components={{
                      DropdownIndicator: CustomDropdownIndicator,
                    }}
                    options={recurringRuleOptions}
                    onChange={onChangeRecurringRule}
                    value={
                      recurringRuleOptions
                        .map(({ key, label }) => ({ value: key, label }))
                        .find((option) => option.value === formState.recurringRule) || null
                    }
                    onInputChange={() => {}}
                    controlStyle={{
                      minWidth: 243,
                      borderRadius: RadiusConfig.sm,
                    }}
                    extraStyle={{
                      indicatorSeparator: (styles) => ({
                        ...styles,
                        display: 'none',
                      }),
                      container: (styles) => ({
                        ...styles,
                        minWidth: 243,
                      }),
                    }}
                    isSearchable={false}
                  />
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      borderRadius: 12,
                      border: `1px solid ${COLOR.neutralTrans['neutralTrans-25']}`,
                      padding: '10px 14px',
                      cursor: 'pointer',
                      height: 44,
                    }}
                    onClick={() => {
                      openModal(MODAL_KEY.END_REPEAT);
                    }}
                  >
                    <Typography variant="body-2" color={COLOR.surfaceNeutralBold['neutral-11']}>
                      {formState.count
                        ? `End after ${formState.count} occurrences`
                        : `End on ${formState?.recurringEnd ? formatDate(new Date(formState?.recurringEnd)) : ''}`}
                    </Typography>
                    <CollapseIcon />
                  </div>
                </div>
              )}
            </RecurringSelect>

            {!isEmpty(errorMessage.recurringDate) && (
              <Typography
                variant="body-3"
                color={COLOR_CONFIG.red}
                style={{
                  margin: '4px 0 0 38px',
                }}
              >
                {errorMessage.recurringDate}
              </Typography>
            )}
          </div>
        )}

        {Number(formState.eventType) === EventType.Class && (
          <RowItem icon={ClassIcon} isDisabled={!isEmpty(eventSessionId)}>
            <CommonSelect
              extraStyle={{
                indicatorSeparator: (styles) => ({
                  ...styles,
                  display: 'none',
                }),
              }}
              isBorderInput
              value={{
                value: selectedClass?.id ?? '',
                label: selectedClass?.id ? (
                  <Typography
                    variant="body-2"
                    color={
                      !isEmpty(eventSessionId)
                        ? COLOR_CONFIG['neutral-text']
                        : COLOR.surfaceNeutralBold['neutral-11']
                    }
                  >
                    {selectedClass.name}
                  </Typography>
                ) : (
                  <Typography variant="body-2" color={COLOR_CONFIG['neutral-text']}>
                    Select class
                  </Typography>
                ),
                subTitle: selectedClass?.id ? selectedClass.code : '',
              }}
              options={classes.map((el) => ({
                value: el.id,
                label: el.name,
                subTitle: el.code,
                stationId: el.stationId,
              }))}
              controlStyle={{
                background: !isEmpty(eventSessionId) ? COLOR.neutralTrans['neutralTrans-3'] : '',
                padding: '10px 6px',
                height: 60,
              }}
              disabled={!isEmpty(eventSessionId)}
              onChange={onChangeClass}
              onInputChange={() => {}}
              placeholder="Select class"
              getOptionLabel={(option: any) => option.label}
              formatOptionLabel={(option: any) => getLabel(option.label, option.subTitle)}
            />
          </RowItem>
        )}
        {Number(formState.eventType) === EventType.Class ? (
          <RowItem
            icon={LessonIcon}
            isDisabled={isEmpty(selectedClass) || !isEmpty(eventSessionId)}
          >
            <CommonSelect
              isBorderInput
              noOptionsMessage={() => (
                <Typography variant="body-2" color={COLOR_CONFIG['neutral-text']}>
                  No lessons or assessments found. Add them to the class first to select them here
                </Typography>
              )}
              extraStyle={{
                indicatorSeparator: (styles) => ({
                  ...styles,
                  display: 'none',
                }),
              }}
              controlStyle={{
                background:
                  isEmpty(selectedClass) || !isEmpty(eventSessionId)
                    ? COLOR.neutralTrans['neutralTrans-3']
                    : '',
                padding: '10px 6px',
                height: 60,
              }}
              value={{
                value: selectedContent?.id ?? '',
                label: selectedContent?.id ? (
                  <Typography
                    variant="body-2"
                    color={
                      isEmpty(selectedClass) || !isEmpty(eventSessionId)
                        ? COLOR_CONFIG['neutral-text']
                        : COLOR.surfaceNeutralBold['neutral-11']
                    }
                  >
                    {selectedContent.name}
                  </Typography>
                ) : (
                  <Typography variant="body-2" color={COLOR_CONFIG['neutral-text']}>
                    Select lesson/assessment
                  </Typography>
                ),
                subTitle: selectedContent?.id ? selectedContent.type : '',
              }}
              options={contents.map((el) => ({
                value: el.id,
                label: el.name,
                subTitle: el.type,
                classContentId: el.classContentId,
              }))}
              disabled={isEmpty(selectedClass) || !isEmpty(eventSessionId)}
              onChange={onChangeContent}
              onInputChange={() => {}}
              placeholder="Select lesson/assessment"
              getOptionLabel={(option: any) => option.label}
              formatOptionLabel={(option: any) => getLabel(option.label, option.subTitle)}
            />
          </RowItem>
        ) : Number(formState.eventType) === EventType.Gamification ? (
          <RowItem icon={CategoryIcon}>
            <CommonSelect
              isBorderInput
              value={formState.selectedCategory ?? null}
              options={categoyOptions}
              onChange={onChangeCategory}
              onInputChange={() => {}}
              placeholder="Select category"
              extraStyle={{
                indicatorSeparator: (styles) => ({
                  ...styles,
                  display: 'none',
                }),
              }}
            />
          </RowItem>
        ) : null}

        {Number(formState.eventType) !== EventType.Gamification ? (
          <RowItem
            icon={AddressIcon}
            isDisabled={
              !isEmpty(eventSessionId) ||
              (Number(formState.eventType) === EventType.Class &&
                (!isNil(selectedClass?.stationId) ||
                  isUndefined(selectedClass) ||
                  selectedContent?.type?.toLowerCase() === ExerciseContentType.DIY))
            }
          >
            <CommonSelect
              isBorderInput
              extraStyle={{
                indicatorSeparator: (styles) => ({
                  ...styles,
                  display: 'none',
                }),
              }}
              value={{
                value: selectedStation?.id ?? '',
                label:
                  selectedContent?.type?.toLowerCase() === ExerciseContentType.DIY ? (
                    <Typography variant="body-2" color={COLOR_CONFIG['neutral-text']}>
                      No physical venue – this is a DIY lesson
                    </Typography>
                  ) : selectedStation?.name ? (
                    <Typography
                      variant="body-2"
                      color={
                        !isEmpty(eventSessionId) ||
                        (Number(formState.eventType) === EventType.Class &&
                          (isUndefined(selectedClass) ||
                            selectedContent?.type?.toLowerCase() === ExerciseContentType.DIY))
                          ? COLOR_CONFIG['neutral-text']
                          : COLOR.surfaceNeutralBold['neutral-11']
                      }
                    >
                      {selectedStation.name}
                    </Typography>
                  ) : (
                    <Typography variant="body-2" color={COLOR_CONFIG['neutral-text']}>
                      Select center
                    </Typography>
                  ),
                subTitle:
                  selectedContent?.type?.toLowerCase() === ExerciseContentType.DIY
                    ? ''
                    : selectedStation?.id
                      ? ` ID ${selectedStation?.stationId}, ${selectedStation?.address}
                      ${selectedStation?.city} ${selectedStation?.state}, ${selectedStation?.country}
                      ${selectedStation?.postalCode ? selectedStation?.postalCode : ''}`
                      : '',
              }}
              options={stations.map((el) => ({
                value: el.id,
                label: el.name,
                subTitle: ` ID ${el.stationId}, ${el.address} ${el.city} ${el.state}, ${el.country} ${el.postalCode ? el.postalCode : ''}`,
              }))}
              controlStyle={{
                background:
                  !isEmpty(eventSessionId) ||
                  (Number(formState.eventType) === EventType.Class &&
                    (isUndefined(selectedClass) ||
                      selectedContent?.type?.toLowerCase() === ExerciseContentType.DIY))
                    ? COLOR.neutralTrans['neutralTrans-3']
                    : '',
                padding: '10px 6px',
                height: 60,
              }}
              disabled={
                !isEmpty(eventSessionId) ||
                (Number(formState.eventType) === EventType.Class &&
                  (isUndefined(selectedClass) ||
                    selectedContent?.type?.toLowerCase() === ExerciseContentType.DIY))
              }
              onChange={onChangeStation}
              onInputChange={() => {}}
              placeholder="Select center"
              getOptionLabel={(option: any) => option.label}
              formatOptionLabel={(option: any) => getLabel(option.label, option.subTitle)}
            />
          </RowItem>
        ) : (
          <RowItem icon={LevelIcon}>
            <CommonSelect
              isBorderInput
              value={formState.selectedLevel ?? null}
              options={levelOptions}
              onChange={onChangeLevel}
              onInputChange={() => {}}
              placeholder="Select level"
              extraStyle={{
                indicatorSeparator: (styles) => ({
                  ...styles,
                  display: 'none',
                }),
              }}
            />
          </RowItem>
        )}

        {Number(formState.eventType) === EventType.Gamification && (
          <RowItem icon={DescriptionIcon}>
            <Textarea
              placeholder="What participants need to do in the challenge"
              value={formState.description}
              onChange={handleChangeDescription}
              style={{
                backgroundColor: COLOR_CONFIG.white,
                borderRadius: RadiusConfig.sm,
                minHeight: 90,
                fontSize: 16,
                lineHeight: '22px',
                color: isEmpty(formState.description)
                  ? COLOR_CONFIG['neutral-text']
                  : COLOR.surfaceNeutralBold['neutral-11'],
              }}
            />
          </RowItem>
        )}

        <RowItem icon={NotiIcon}>
          {isReminder.value ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                border: `1px solid ${COLOR.neutralTrans['neutralTrans-25']}`,
                borderRadius: 12,
                padding: '10px 14px',
                width: '100%',
                cursor: 'pointer',
              }}
              onClick={() => isReminder.setValue(false)}
            >
              <Typography variant="body-2" color={COLOR.surfaceNeutralBold['neutral-11']}>
                1 day before (9:00 AM)
              </Typography>
              <CloseIcon />
            </div>
          ) : (
            <div
              style={{
                padding: '0px 14px',
                border: `1px solid ${COLOR.purpel['purpel-6']}`,
                borderRadius: RadiusConfig.sm,
                height: 44,
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
              }}
              onClick={() => isReminder.setValue(true)}
            >
              <Typography variant="body-2" color={COLOR.purpel['purpel-6']} weight={600}>
                Add reminder
              </Typography>
            </div>
          )}
        </RowItem>

        {Number(formState.eventType) !== EventType.Gamification ? (
          <RowItem icon={DescriptionIcon}>
            <Textarea
              placeholder="Add description (optional)"
              style={{
                backgroundColor: COLOR_CONFIG.white,
                borderRadius: RadiusConfig.sm,
                minHeight: 90,
                fontSize: 16,
                lineHeight: '22px',
                color: isEmpty(formState.description)
                  ? COLOR_CONFIG['neutral-text']
                  : COLOR.surfaceNeutralBold['neutral-11'],
              }}
              value={formState.description}
              onChange={handleChangeDescription}
            />
          </RowItem>
        ) : (
          <RowItem icon={PhotoIcon}>
            <CustomFileUpload>
              {thumbnailUrl.value ? (
                <div style={{ maxWidth: 512 }}>
                  <img
                    onLoad={handleGetWidthAndHeightThumbnail}
                    src={
                      thumbnailUrl.value && !thumbnailUrl.value.includes('thumbnail-default')
                        ? thumbnailUrl.value
                        : ''
                    }
                    alt="thumbnail"
                    style={{ maxWidth: 432, maxHeight: 140, objectFit: 'contain' }}
                  />
                  <Typography
                    variant="body-3"
                    color={COLOR_CONFIG.red}
                    onClick={handleRemoveThumbnail}
                    style={{ marginTop: 8, cursor: 'pointer' }}
                  >
                    Remove image
                  </Typography>
                </div>
              ) : (
                <FileUpload
                  id={'thumbnail'}
                  icon={<UploadIcon />}
                  title="Upload event image"
                  onChangeFiles={onChangeThumbnail}
                  accept=".png,.jpeg,.svg,.webp"
                  style={{ height: 140 }}
                />
              )}
            </CustomFileUpload>
          </RowItem>
        )}

        <Row>
          {Number(formState.eventType) === EventType.Class && !selectedClass ? (
            <Row align="center" className="gap-16">
              <UserIcon />
              <Typography variant="body-2" color={COLOR_CONFIG['neutral-text']}>
                Select class to view participant list
              </Typography>
            </Row>
          ) : (
            <InviteeSelector
              selectedInvitees={selectedInvitees}
              onInviteeChange={setSelectedInvitees}
              availableInvitees={formState.invitees}
              onLoadMoreClients={getListClients}
              onLoadMoreTrainers={getListTrainers}
              isLoadingClients={inviteeState.isLoadingClients}
              isLoadingTrainers={inviteeState.isLoadingTrainers}
              hasMoreClients={inviteeState.hasMoreClients}
              hasMoreTrainers={inviteeState.hasMoreTrainers}
              onTabChange={handleTabChange}
              onDropdownOpen={handleDropdownOpen}
              allowLoadMore={handleInviteeScroll()}
              isShowInviteesWithoutSelected={Number(formState.eventType) === EventType.Class}
            />
          )}
        </Row>

        <EndRepeatModal
          calendarKey={calendarKey}
          onCancel={handleCloseModal(MODAL_KEY.END_REPEAT)}
          value={{
            date: formState.recurringEnd,
            occurrences: formState.count,
            on: endRepeatValue.value === 'on',
            after: endRepeatValue.value === 'after',
          }}
          onChangeChecked={onChangeEndRepeatValue}
          onChangeOccurrences={onChangeOccurrences}
          onChangeDate={onChangeDate}
          onSubmit={handleEndRepeatSubmit}
        />
        <EditRecurringEventModal
          onCancel={handleCloseModal(MODAL_KEY.EDIT_RECURRING_EVENT)}
          value={formState.applyOption}
          onChangeChecked={onChangeApplyOption}
          onSubmit={handleSubmitEditRecurringEvent(MODAL_KEY.EDIT_RECURRING_EVENT)}
        />

        <EditRecurringNotUpdatedEventModal
          onCancel={handleCloseModal(MODAL_KEY.EDIT_RECURRING_NOT_UPDATED_EVENT)}
          onSubmit={handleSubmitEditRecurringEvent(MODAL_KEY.EDIT_RECURRING_NOT_UPDATED_EVENT)}
        />

        <DeleteRecurringEventModal
          onCancel={handleCloseModal(MODAL_KEY.DELETE_RECURRING_EVENT)}
          value={formState.applyOption}
          onChangeChecked={onChangeApplyOption}
          onRemove={handleDeleteEvent}
        />

        <DeleteRecurringNotUpdatedEventModal
          onCancel={handleCloseModal(MODAL_KEY.DELETE_RECURRING_NOT_UPDATED_EVENT)}
          onRemove={handleDeleteEvent}
        />

        <QRCodeModal value={formState.qrKey} />
      </div>
    </div>
  );
};
